import { useEffect, useState } from 'react'
import { useMatch, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AdminFetchOnboardingTemplate, FlowEditorInitCreateDraft } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCollectStepReferenceLoading } from 'src/selectors/collectStepReference'
import {
  getFlowEditorError,
  getFlowEditorLoading,
  getFlowEditorSavedSnapshot,
  getFlowEditorSaving,
  getFlowEditorTemplate,
} from 'src/selectors/onboardingFlowEditor'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { buildCollectStepReferenceRequests } from 'src/utils/collectStepReferenceRequests'
import { logger } from 'src/utils/logger'

export function useFlowEditorBootstrap() {
  const { customerId = '', templateId = '' } = useParams<{
    customerId: string
    templateId: string
  }>()
  // Derived from the route, not from an empty `templateId`: a malformed or truncated URL must not
  // silently become new-template mode.
  const isCreateMode = useMatch(ADMIN_ROUTES.FLOW_EDITOR_NEW_URL) !== null
  const dispatch = useDispatch()

  const template = useSelector(getFlowEditorTemplate)
  const savedSnapshot = useSelector(getFlowEditorSavedSnapshot)
  const loading = useSelector(getFlowEditorLoading)
  const collectStepRefsLoading = useSelector(getCollectStepReferenceLoading)
  const saving = useSelector(getFlowEditorSaving)
  const error = useSelector(getFlowEditorError)
  // Each step-picker list is a separate GET with its own view permission (ENG-1207 part 2).
  const canViewDocusignTemplates = useHasPermission(InternalPermission.DocusignTemplatesView)
  const canViewLmsTrainings = useHasPermission(InternalPermission.LmsTrainingsView)
  const canViewYardstik = useHasPermission(InternalPermission.YardstikView)
  const canViewInsurancePolicies = useHasPermission(InternalPermission.InsurancePoliciesView)

  const [collectStepRefsPrimed, setCollectStepRefsPrimed] = useState(false)

  useEffect(() => {
    if (!customerId) {
      setCollectStepRefsPrimed(true)

      return
    }

    let cancelled = false

    // Only the lists this user may read. A skipped list is absent from `Promise.all`, so priming
    // never waits on a request that was never dispatched; with none permitted it resolves at once
    // and the template loads straight away.
    const requests = buildCollectStepReferenceRequests(customerId, {
      canViewDocusignTemplates,
      canViewLmsTrainings,
      canViewYardstik,
      canViewInsurancePolicies,
    })

    setCollectStepRefsPrimed(false)

    void (async () => {
      try {
        await Promise.all(
          requests.map((request) => {
            return createAsyncAction(dispatch, request)
          }),
        )
      } catch {
        logger.warn('Collect step reference lists failed to load', { customerId })
      } finally {
        if (!cancelled) {
          setCollectStepRefsPrimed(true)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    customerId,
    dispatch,
    canViewDocusignTemplates,
    canViewLmsTrainings,
    canViewYardstik,
    canViewInsurancePolicies,
  ])

  useEffect(() => {
    if (!templateId || !collectStepRefsPrimed) {
      return
    }

    dispatch(AdminFetchOnboardingTemplate.request({ templateId }))
  }, [templateId, collectStepRefsPrimed, dispatch])

  useEffect(() => {
    if (!isCreateMode || !collectStepRefsPrimed || !customerId) {
      return
    }

    dispatch(FlowEditorInitCreateDraft({ partnerId: customerId }))
  }, [collectStepRefsPrimed, customerId, dispatch, isCreateMode])

  const pageBlockingLoading =
    (Boolean(customerId) && !collectStepRefsPrimed) || collectStepRefsLoading || loading

  return {
    collectStepRefsPrimed,
    customerId,
    error,
    isCreateMode,
    pageBlockingLoading,
    savedSnapshot,
    saving,
    template,
    templateId,
  }
}
